const STORAGE_KEY = "neon-arena:bindings";

export type Action =
  | "MOVE_FORWARD"
  | "MOVE_BACKWARD"
  | "TURN_LEFT"
  | "TURN_RIGHT"
  | "HANDBRAKE"
  | "BOOST"
  | "FIRE"
  | "FIRE2"
  | "DROP_WEAPON"
  | "LOOK_BEHIND"
  | "CAMERA_TOGGLE";

export const GAME_ACTIONS: Action[] = [
  "MOVE_FORWARD",
  "MOVE_BACKWARD",
  "TURN_LEFT",
  "TURN_RIGHT",
  "HANDBRAKE",
  "BOOST",
  "FIRE",
  "FIRE2",
  "DROP_WEAPON",
];

export const CAMERA_ACTIONS: Action[] = [
  "LOOK_BEHIND",
  "CAMERA_TOGGLE",
];

export const ACTIONS: Action[] = [...GAME_ACTIONS, ...CAMERA_ACTIONS];

export const ACTION_LABELS: Record<Action, string> = {
  MOVE_FORWARD:  "MOVE FORWARD",
  MOVE_BACKWARD: "MOVE BACKWARD",
  TURN_LEFT:     "TURN LEFT",
  TURN_RIGHT:    "TURN RIGHT",
  HANDBRAKE:     "HANDBRAKE",
  BOOST:         "BOOST",
  FIRE:          "FIRE",
  FIRE2:         "SECONDARY FIRE",
  DROP_WEAPON:   "DROP WEAPON",
  LOOK_BEHIND:   "LOOK BEHIND",
  CAMERA_TOGGLE: "CAMERA VIEW",
};

export interface Bindings {
  keyboard: Record<Action, string>;
  gamepad: Record<Action, string>;
}

/** Keyboard binding values are KeyboardEvent.code strings, or "MouseLeft". */
export const DEFAULT_KEYBOARD: Record<Action, string> = {
  MOVE_FORWARD:  "KeyW",
  MOVE_BACKWARD: "KeyS",
  TURN_LEFT:     "KeyA",
  TURN_RIGHT:    "KeyD",
  HANDBRAKE:     "ShiftLeft",
  BOOST:         "KeyE",
  FIRE:          "Space",
  FIRE2:         "KeyQ",
  DROP_WEAPON:   "KeyF",
  LOOK_BEHIND:   "KeyC",
  CAMERA_TOGGLE: "KeyV",
};

/**
 * Gamepad binding values:
 *   "btn<N>"    — button index (pressed / analog value)
 *   "axis<N>+"  — axis index, positive side
 *   "axis<N>-"  — axis index, negative side
 *
 * Xbox standard layout: btn0=A, btn1=B, btn2=X, btn3=Y,
 *   btn4=LB, btn5=RB, btn6=LT, btn7=RT, btn9=Start,
 *   axis0=LX, axis1=LY.
 */
export const DEFAULT_GAMEPAD: Record<Action, string> = {
  MOVE_FORWARD:  "btn7",   // RT
  MOVE_BACKWARD: "btn6",   // LT
  TURN_LEFT:     "axis0-", // Left stick left
  TURN_RIGHT:    "axis0+", // Left stick right
  HANDBRAKE:     "btn1",   // B / Circle
  BOOST:         "btn5",   // RB / R1
  FIRE:          "btn0",   // A / Cross
  FIRE2:         "btn2",   // X / Square
  DROP_WEAPON:   "btn4",   // LB / L1
  LOOK_BEHIND:   "btn11",  // R3 (right stick click)
  CAMERA_TOGGLE: "btn3",   // Y / Triangle
};

function makeDefaults(): Bindings {
  return {
    keyboard: { ...DEFAULT_KEYBOARD },
    gamepad: { ...DEFAULT_GAMEPAD },
  };
}

let _current: Bindings = loadFromStorage();

export function getBindings(): Bindings {
  return _current;
}

export function setKeyboardBinding(action: Action, key: string): void {
  for (const a of ACTIONS) {
    if (a !== action && _current.keyboard[a] === key) _current.keyboard[a] = "";
  }
  _current.keyboard[action] = key;
  persist();
}

export function setGamepadBinding(action: Action, binding: string): void {
  for (const a of ACTIONS) {
    if (a !== action && _current.gamepad[a] === binding) _current.gamepad[a] = "";
  }
  _current.gamepad[action] = binding;
  persist();
}

export function resetKeyboardBindings(): void {
  _current.keyboard = { ...DEFAULT_KEYBOARD };
  persist();
}

export function resetGamepadBindings(): void {
  _current.gamepad = { ...DEFAULT_GAMEPAD };
  persist();
}

export function resetBindings(): void {
  _current = makeDefaults();
  persist();
}

function persist(): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(_current));
  } catch {
    // ignore — private browsing or storage full
  }
}

function loadFromStorage(): Bindings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return makeDefaults();
    const parsed = JSON.parse(raw) as Partial<Bindings>;
    return {
      keyboard: { ...DEFAULT_KEYBOARD, ...(parsed.keyboard ?? {}) } as Record<Action, string>,
      gamepad: { ...DEFAULT_GAMEPAD, ...(parsed.gamepad ?? {}) } as Record<Action, string>,
    };
  } catch {
    return makeDefaults();
  }
}

/** Human-readable label for a keyboard binding code. */
export function formatKeyLabel(code: string): string {
  if (!code) return "—";
  if (code === "MouseLeft") return "LMB";
  const MAP: Record<string, string> = {
    Space: "SPACE", ShiftLeft: "L.SHIFT", ShiftRight: "R.SHIFT",
    ControlLeft: "L.CTRL", ControlRight: "R.CTRL",
    AltLeft: "L.ALT", AltRight: "R.ALT",
    ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→",
    Enter: "ENTER", Backspace: "BKSP", Tab: "TAB", Escape: "ESC",
    CapsLock: "CAPS",
  };
  if (MAP[code]) return MAP[code];
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Numpad")) return `NUM${code.slice(6)}`;
  if (code.startsWith("F") && /^\d+$/.test(code.slice(1))) return code;
  return code.toUpperCase();
}

/** Human-readable label for a gamepad binding string. */
export function formatGpLabel(binding: string): string {
  if (!binding) return "—";
  if (binding.startsWith("btn")) {
    const idx = parseInt(binding.slice(3));
    const NAMES: Record<number, string> = {
      0: "A", 1: "B", 2: "X", 3: "Y",
      4: "LB", 5: "RB", 6: "LT", 7: "RT",
      8: "SELECT", 9: "START", 10: "L3", 11: "R3",
    };
    return NAMES[idx] ?? `BTN${idx}`;
  }
  if (binding.startsWith("axis")) {
    const idx = parseInt(binding.slice(4, -1));
    const dir = binding.endsWith("+") ? "+" : "−";
    const AXIS: Record<number, string> = { 0: "LX", 1: "LY", 2: "RX", 3: "RY" };
    return `${AXIS[idx] ?? `AX${idx}`}${dir}`;
  }
  return binding.toUpperCase();
}
